import {
  useEffect,
  useRef,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  EventMarkerIcon,
  DirectionIcon,
  MarkerGenericIcon,
  SaveIcon,
  ShareEventIcon,
  TagIcon,
  AffinityIcon,
  AddCalendarIcon
} from 'assets/icons'
import {
  Box,
  Image,
  Link,
  Skeleton,
  Spacer,
  Stack,
  Text
} from '@chakra-ui/react'
import {
  getNotificationMessage,
  markersSelectors,
  saveEvent,
  setBlogLinkToBeCopied,
  setEventData,
  setOpenChatModal,
  setSaveEventStatus,
  setSharingToFriends,
  toggleImageCarousel,
  usersSelectors
} from 'store'
import { LoadingSpinier } from 'common/Loading'
import { ImagesPng } from 'assets/images'
import {
  convertToOwnTimeZone,
  convertToOwnTimeZoneEvent,
  decimalToDMS,
  convertDatetoICSFormat
} from 'utils/helpers'
import {
  PENDING,
  REJECTED,
  SUCCEEDED
} from 'utils/constants'
import { createEvent } from 'ics'

export const Content = () => {
  const dispatch = useDispatch()

  const imageRefs = useRef([])

  const {
    getEventDataStatus,
    eventData
  } = useSelector(markersSelectors)
  const {
    activeProfile,
    saveEventErrorMsg,
    savedEventId,
    saveEventStatus
  } = useSelector(usersSelectors)

  const [directionAddress, setDirectionAddress] = useState('')
  const [saveFontColor, setSaveFontColor] = useState('black')
  const [loadedBI, setLoadedBI] = useState(false)
  const [loadedAI, setLoadedAI] = useState({})

  const formattedSD = convertToOwnTimeZone(eventData?.start_date, 'Y.m.d')
  const formattedED = convertToOwnTimeZone(eventData?.end_date, 'Y.m.d')
  const icsSD = convertDatetoICSFormat(convertToOwnTimeZoneEvent(eventData?.start_date))
  const icsED = convertDatetoICSFormat(convertToOwnTimeZoneEvent(eventData?.end_date))
  const daterange = `${formattedSD}/${formattedED}`
  const deviceSpec = navigator.userAgent
  const customPopupOverlay = document.querySelector('#custom-popup-overlay-event')

  const idText = eventData?.id.toLocaleString('en-US', {
    minimumIntegerDigits: 3,
    useGrouping: false
  })

  const convertedCoordinates = () => {
    let convertedLat = decimalToDMS(eventData?.lat, 'lat')
    let convertedLng = decimalToDMS(eventData?.lng, 'lng')

    return convertedLat + ' ' + convertedLng
  }

  const addToRefs = (el) => {
    if (el && !imageRefs?.current.includes(el)) {
      imageRefs?.current.push(el)
    }
  }
  const alarms = [
    {
      action: 'display',
      description: 'An event is about to start in 7 days. Be ready!',
      trigger: { days: 7, before: true },
      repeat: 2
    },
    {
      action: 'display',
      description: 'An event is about to start in 1 day. Be ready!',
      trigger: { hours: 24, before: true },
      repeat: 2
    }
  ]

  const eventICSData = {
    start: icsSD,
    end: icsED,
    title: eventData?.name,
    description: eventData?.description,
    location: eventData?.address || '',
    geo: {
      lat: eventData?.lat,
      lng: eventData?.lng
    },
    categories: [eventData?.affinities],
    url: eventData?.affiliate_link,
    alarms
  }

  const handleShare = (id) => {
    sessionStorage.setItem('isSharingBlog', 'true')
    window.dispatchEvent(new CustomEvent('isSharingBlog-changed'))

    if (customPopupOverlay) {
      customPopupOverlay.click()
    }

    setTimeout(() => {
      dispatch(setOpenChatModal(true))
      dispatch(setSharingToFriends(true))
      dispatch(setBlogLinkToBeCopied(window.location.origin + '/?eventid=' + id))
      setTimeout(() => dispatch(setOpenChatModal(false)), 300)
    }, customPopupOverlay ? 800 : 0)
  }

  useEffect(() => {
    if (
      deviceSpec.includes('iPhone')
      || deviceSpec.includes('Mac')
      || deviceSpec.includes('iPad')
    ) {
      if (eventData?.address !== null && eventData?.address !== '') {
        setDirectionAddress(`http://maps.apple.com/?daddr=${encodeURIComponent(eventData?.address)}`)
      } else if (eventData?.address === null || eventData?.address === '') {
        setDirectionAddress(`https://maps.apple.com/?daddr=${eventData?.lat},${eventData?.lng}`)
      }
    } else if (eventData?.address !== null && eventData?.address !== undefined) {
      setDirectionAddress(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(eventData?.address)}`)
    } else if (eventData?.address === null || eventData?.address === '') {
      setDirectionAddress(`https://www.google.com/maps/dir/${activeProfile?.coordinates[0]},${activeProfile?.coordinates[1]}/${eventData?.lat},${eventData?.lng}`)
    }
  }, [eventData?.address])

  useEffect(() => {
    if (
      eventData?.id === savedEventId
      && (
        saveEventStatus === PENDING
        || saveEventStatus === REJECTED
      )
    ) {
      if (
        saveEventErrorMsg === 'The event is unavailable'
        && saveEventStatus === REJECTED
      ) {
        document.querySelector('#force-close-event-drawer')?.click()
        document.querySelector('#custom-popup-overlay-event')?.click()
        dispatch(setSaveEventStatus(''))
      } else {
        if (!eventData?.savedByMe) {
          setSaveFontColor('rgb(231,108,49)')
          setTimeout(() => setSaveFontColor('black'), 1000)
        }

        dispatch(setEventData({
          ...eventData,
          savedByMe: !eventData?.savedByMe
        }))
      }
    }
  }, [saveEventStatus])

  useEffect(() => {
    if (getEventDataStatus === SUCCEEDED && !eventData?.id) {
      document.querySelector('#force-close-event-drawer')?.click()

      dispatch(setEventData(null))
      dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: 'Event Unavailable',
          isEditProfile: false
        })
      )
    }
  }, [getEventDataStatus])

  return (
    getEventDataStatus === PENDING || !eventData?.id
      ? (
        <Stack
          position="absolute"
          height="100%"
          width="100%"
          marginTop="-6svh"
        >
          <LoadingSpinier />
        </Stack>
      ) : (
        <>
          <Stack height="90px !important" className="event-popup-division" id="event-header-popup">
            <Skeleton
              isLoaded={loadedBI}
              fadeDuration={1}
            >
              <Image
                height="90px !important"
                width="100%"
                objectFit="cover"
                padding="0px 0px"
                id="event-name-popup"
                src={eventData?.background_image ?? ImagesPng.PlaceholderSquare}
                onLoad={() => setLoadedBI(true)}
              />
            </Skeleton>
          </Stack>

          <Stack className="event-popup-division" id="event-name-popup">
            <Text maxWidth="80%">{eventData?.name ? eventData?.name : 'Event Unavailable. Please refresh page.'}</Text>
            <Spacer />
            <Text className="event-id-text">{idText}</Text>
          </Stack>

          <Stack padding="0px 20px" className="event-popup-division" id="event-description-popup">
            <Stack className="event-division-item-wrapper-popup">
              <Text maxWidth="90%">{eventData?.description ? eventData?.description : 'No Description'}</Text>
            </Stack>
          </Stack>

          <Stack
            height="50px"
            className="event-popup-division"
            id="event-date-popup"
            cursor="pointer"
            onClick={() => createEvent(eventICSData, (error, value) => {
              if (error) {
                console.log(error)
                return
              }

              const blob = new Blob([value], { type: 'text/calendar;charset=utf-8' })
              const href = URL.createObjectURL(blob)
              const link = document.createElement('a')
              link.href = href
              link.setAttribute('download', `${eventICSData.title}.ics`)
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
              URL.revokeObjectURL(href)

              // console.log(value)
            })}
          >
            <Box
              height="50px"
              className="event-division-item-wrapper-popup"
            >
              <EventMarkerIcon start_date={formattedSD.split('.')[1]} color="#000" />
              <Text>{eventData?.start_date && eventData.end_date ? daterange : 'null'}</Text>
              <Spacer />
              <AddCalendarIcon />
            </Box>
          </Stack>

          <Stack height="50px" className="event-popup-division" id="event-address-popup">
            <Box height="50px" className="event-division-item-wrapper-popup">
              <MarkerGenericIcon />
              <Text maxWidth="288px" marginY="5px !important">
                {
                  eventData?.address === null || eventData?.address === ''
                    ? convertedCoordinates()
                    : eventData?.address
                }
              </Text>
            </Box>
          </Stack>

          <Stack height="50px" className="event-popup-division">
            <Box height="50px" className="event-division-item-wrapper-popup">
              <Link
                href={directionAddress}
                target="_blank"
                _cursor="pointer"
                style={{
                  textDecoration: 'none',
                  color: '#000'
                }}
              >
                <DirectionIcon />
                <Text>Direction (Maps)</Text>
              </Link>
            </Box>
          </Stack>

          <Stack height="50px" className="event-popup-division">
            <Link
              href={eventData?.affiliate_link}
              target="_blank"
              _cursor="pointer"
              style={{
                textDecoration: 'none',
                color: '#000'
              }}
            >
              <TagIcon />
              <Text>Tickets (Discount)</Text>
            </Link>
          </Stack>

          <Stack height="50px" className="event-popup-division">
            <Box
              height="50px"
              className="event-division-item-wrapper-popup"
              cursor="pointer"
              onClick={() => handleShare(eventData?.id)}
            >
              <ShareEventIcon />
              <Text>Share</Text>
            </Box>
          </Stack>

          <Stack height="50px" className="event-popup-division">
            <Box
              as="button"
              height="50px"
              textAlign="left"
              className="event-division-item-wrapper-popup"
              cursor={saveEventStatus === PENDING ? 'wait' : 'pointer'}
              onClick={() => {
                if (saveEventStatus !== PENDING && eventData?.id) {
                  if (!eventData?.savedByMe) {
                    setSaveFontColor('rgb(231,108,49)')
                    setTimeout(() => setSaveFontColor('black'), 1000)
                  }

                  dispatch(saveEvent({
                    id: eventData?.id,
                    action: eventData?.savedByMe ? 'remove' : 'save'
                  }))
                }
              }}
            >
              <Box flexShrink={0}>
                <SaveIcon
                  width="22"
                  height="22"
                  viewBox="0 2 22 22"
                />
              </Box>
              <Text color={saveFontColor}>
                {
                  eventData?.savedByMe
                    ? 'Saved in Events row of Notifications Panel'
                    : 'Save'
                }
              </Text>
            </Box>
          </Stack>

          <Stack className="event-popup-division">
            <Box
              height="50px"
              className="event-division-item-wrapper-popup"
            >
              <AffinityIcon />
              <Box className="event-popup-affinities-container">
                {eventData?.affinities}
              </Box>
            </Box>
          </Stack>

          <Stack id="event-popup-image-wrapper">
            <Stack id="event-popup-image">
              {eventData?.additional_images?.map((e, index) => (
                <Skeleton
                  isLoaded={!!loadedAI[e]}
                  fadeDuration={1}
                >
                  <Image
                    key={e}
                    ref={addToRefs}
                    src={e}
                    maxWidth="320px"
                    borderRadius="5px"
                    cursor="pointer"
                    onClick={() => {
                      dispatch(toggleImageCarousel(true))
                      localStorage.setItem('image-current-slide', index)
                    }}
                    onLoad={() => setLoadedAI((prev) => ({
                      ...prev,
                      [e]: true
                    }))}
                  />
                </Skeleton>
              ))}
            </Stack>
          </Stack>
        </>
      )
  )
}
